import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { json, Route, Routes } from "react-router-dom";
import { usePermission, useUser } from "../account/AccountStore";
import { Box, FormField, Grid, Heading, ResponsiveContext, Tab, Tabs, Text, TextInput } from "grommet";
import { actionAgent, AgentConfig, AgentViewModel, getAgents, LEDBarConfig } from "./AgentsStore";
import { createEvent, CreateEventRequest, EventViewModel, getEvents, updateEvent, UpdateEventRequest } from "../events/EventsStore";
import EventSearch from "../events/EventSearch";
import CustomButton from "../components/CustomButton";
import StyledModal from "../components/StyledModel";
import { drop, keys, set, toPairs, uniq, update, values, zipObject } from "lodash";
import PlusMinus from "../components/PlusMinus";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import AgentTile from "./AgentTile";
import AgentControls from "./AgentControls";
import LEDBarAction, { RenderLEDBarAction } from "./LEDBarAction";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const minus = <FontAwesomeIcon icon={["fad", "minus"]}></FontAwesomeIcon>;
const pointer = <FontAwesomeIcon icon={["fad", "mouse-pointer"]}></FontAwesomeIcon>;

interface TeamConfig {
    name: number;
    agents: AgentConfig[];
}

const AgentsManagement = () => {
    const user = useUser();

    const [events, setEvents] = React.useState([] as EventViewModel[]);
    const [agents, setAgents] = React.useState([] as AgentViewModel[]);

    const [selectedEvent, setSelectedEvent] = React.useState({} as EventViewModel);

    const [showCreateEventModal, setShowCreateEventModal] = React.useState(false);
    const [newEventName, setNewEventName] = React.useState("");

    React.useEffect(() => {
        load();
    }, []);

    const load = () => {
        getAgents("").then((response) => {
            setAgents(response);
        });

        getEvents("").then((response) => {
            setEvents(response);
        });
    };

    const doCreateEvent = () => {
        createEvent({ name: newEventName } as CreateEventRequest).then((response) => {
            load();
            setNewEventName("");
        });
    };

    const [teamCount, setTeamCount] = React.useState(2);
    const [teams, setTeams] = React.useState([] as TeamConfig[]);

    React.useEffect(() => {
        if (selectedEvent.id) {
            // let maxTeams = keys(selectedEvent.config.agents).reduce((acc, key) => {
            //     return acc + selectedEvent.config.agents[key].team;
            // }, 1);

            // setTeamCount(maxTeams);
            updateTeams();
        }
    }, [selectedEvent]);

    // React.useEffect(() => {
    //     updateTeams();
    // }, [teamCount]);

    const updateTeams = () => {
        let teams = new Array(teamCount).fill(0).map(
            (x, i) =>
                ({
                    name: i + 1,
                    agents: keys(selectedEvent.config?.agents)
                        .filter((key) => selectedEvent.config.agents[key].team == i)
                        .map((a) => selectedEvent.config.agents[a]),
                } as TeamConfig)
        );
        setTeams(teams);
    };

    const handleDrop = (droppedItem) => {
        if (!droppedItem.destination || droppedItem.destination.droppableId == "x") return;

        let event = { ...selectedEvent };

        let agent = {
            player: +droppedItem.destination.index,
            team: +droppedItem.destination.droppableId,
            agentID: droppedItem.draggableId,
            source: 3,
            staticUrl: staticUrl,
            version: 1,
        } as AgentConfig;

        event.config.agents[droppedItem.draggableId] = agent;

        updateEvent({ id: event.id, name: event.name, config: event.config } as UpdateEventRequest);
        setSelectedEvent(event);
    };

    const handleAction = (id, agentConfig, ledBarConfig) => {
        let event = { ...selectedEvent };

        agentConfig = applyStaticUrl(staticUrl, { ...event.config.agents[id], ...agentConfig });

        event.config.agents[id] = agentConfig;
        updateEvent({ id: event.id, name: event.name, config: event.config } as UpdateEventRequest);
        setSelectedEvent(event);

        actionAgent(id, { ...agentConfig }, { ...ledBarConfig, id: agentConfig.ledBarID });
    };

    const handleTeamAction = (teamIndex, agentConfig, ledBarConfig) => {
        let agents = keys(selectedEvent.config.agents).filter((key) => selectedEvent.config.agents[key].team == teamIndex);

        let agentConfigs = agents.map((agent) => ({ ...selectedEvent.config.agents[agent], ...agentConfig })).map((agent) => applyStaticUrl(staticUrl, agent));

        let agentMap = zipObject(agents, agentConfigs);

        let event = { ...selectedEvent };

        event.config.agents = { ...event.config.agents, ...agentMap };
        updateEvent({ id: event.id, name: event.name, config: event.config } as UpdateEventRequest);
        setSelectedEvent(event);

        Promise.all(agents.map((agent) => actionAgent(agent, agentMap[agent], { ...ledBarConfig, id: agentMap[agent].ledBarID })));
    };

    const handleAllAction = (agentConfig, ledBarConfig) => {
        let agents = keys(selectedEvent.config.agents);

        let agentConfigs = agents.map((agent) => ({ ...selectedEvent.config.agents[agent], ...agentConfig })).map((agent) => applyStaticUrl(staticUrl, agent));

        let agentMap = zipObject(agents, agentConfigs);

        let event = { ...selectedEvent };

        event.config.agents = { ...event.config.agents, ...agentMap };
        updateEvent({ id: event.id, name: event.name, config: event.config } as UpdateEventRequest);
        setSelectedEvent(event);

        Promise.all(agents.map((agent) => actionAgent(agent, agentMap[agent], null)));
        actionAgent(0, agentConfig, { ...ledBarConfig, id: 0 } as LEDBarConfig);
    };

    const applyStaticUrl = (url: string, agentConfig: AgentConfig) => {
        agentConfig.staticUrl = url.replace("{team}", agentConfig.team.toString()).replace("{player}", agentConfig.player.toString());
        return agentConfig;
    };

    const [staticUrlName, setStaticUrlName] = React.useState("");
    const [staticUrl, setStaticUrl] = React.useState("");

    const addStaticUrl = () => {
        let event = { ...selectedEvent };

        event.config.staticUrls = [...event.config.staticUrls, { name: staticUrlName, url: staticUrl }];
        updateEvent({ id: event.id, name: event.name, config: event.config } as UpdateEventRequest);
        setSelectedEvent(event);
    };

    const addLEDBarAction = () => {
        let event = { ...selectedEvent };

        event.config.ledBarActions[ledBarActionName] = {
            id: 0,
            mode: ledBarActionConfig.mode,
            colorH: ledBarActionConfig.colorH,
            colorS: ledBarActionConfig.colorS,
            colorV: ledBarActionConfig.colorV,
        };
        updateEvent({ id: event.id, name: event.name, config: event.config } as UpdateEventRequest);
        setSelectedEvent(event);
    };

    const removeStaticUrl = (staticUrl) => {
        let event = { ...selectedEvent };

        event.config.staticUrls = event.config.staticUrls.filter((s) => s.url != staticUrl.url);
        updateEvent({ id: event.id, name: event.name, config: event.config } as UpdateEventRequest);
        setSelectedEvent(event);
    };

    const removeLEDBarAction = (ledBarAction) => {
        let event = { ...selectedEvent };

        delete event.config.ledBarActions[ledBarAction];
        updateEvent({ id: event.id, name: event.name, config: event.config } as UpdateEventRequest);
        setSelectedEvent(event);
    };

    const removeAgent = (id) => {
        let event = { ...selectedEvent };

        delete event.config.agents[id];
        updateEvent({ id: event.id, name: event.name, config: event.config } as UpdateEventRequest);
        setSelectedEvent(event);
    };

    const updateAgentPlayer = (agentConfig, player) => {
        let event = { ...selectedEvent };

        let agent = { ...agentConfig, player: player };
        event.config.agents[agentConfig.agentID] = agent;

        updateEvent({ id: event.id, name: event.name, config: event.config } as UpdateEventRequest);
        setSelectedEvent(event);
    };

    const updateAgentLEDID = (agentConfig: AgentConfig, ledID: number) => {
        let event = { ...selectedEvent };

        agentConfig.ledBarID = ledID;

        // if (!agentConfig.ledBarConfig) agentConfig.ledBarConfig = { id: 0, mode: 0, colorH: 0, colorS: 0, colorV: 0 };

        // agentConfig.ledBarConfig.mode = 1;
        // agentConfig.ledBarConfig.colorH = 1;
        // agentConfig.ledBarConfig.colorS = 255;
        // agentConfig.ledBarConfig.colorV = 255;

        // let agent = { ...agentConfig, ledBarActions: [] } as AgentConfig;
        event.config.agents[agentConfig.agentID] = agentConfig;

        updateEvent({ id: event.id, name: event.name, config: event.config } as UpdateEventRequest);
        setSelectedEvent(event);
    };

    const size = React.useContext(ResponsiveContext);

    const [showAgentPlacementModal, setShowAgentPlacementModal] = React.useState(false);

    const [selectedAgent, setSelectedAgent] = React.useState({} as AgentViewModel);

    const [placementPlayer, setPlacementPlayer] = React.useState(0);
    const [placementTeam, setPlacementTeam] = React.useState(0);

    const toggleAgentPlacementModal = (agent) => {
        setSelectedAgent(agent);
        setShowAgentPlacementModal(true);
    };

    const placeAgent = () => {
        let event = { ...selectedEvent };

        let agent = {
            player: placementPlayer,
            team: placementTeam,
            agentID: selectedAgent.id,
            source: 3,
            staticUrl: staticUrl,
            version: 1,
        } as AgentConfig;

        event.config.agents[selectedAgent.id] = agent;

        updateEvent({ id: event.id, name: event.name, config: event.config } as UpdateEventRequest);
        setSelectedEvent(event);

        setShowAgentPlacementModal(false);
        setPlacementPlayer(0);
        setPlacementTeam(0);
    };

    const [ledBarActionName, setLedBarActionName] = React.useState("");
    const [ledBarActionConfig, setLedBarActionConfig] = React.useState({} as LEDBarConfig);

    return (
        <div>
            {user.hasRole("Admin") && (
                <Box>
                    <Grid columns={["auto", "200px"]} gap={"small"}>
                        {events.length > 0 && <EventSearch onSelect={setSelectedEvent}></EventSearch>}
                        <CustomButton icon={plus} label="Create Event" onClick={() => setShowCreateEventModal(true)}></CustomButton>
                    </Grid>

                    <Heading level={3}>Agents {selectedEvent.id && <Text>@ {selectedEvent.name}</Text>}</Heading>

                    {selectedEvent.id && (
                        <>
                            <Tabs>
                                <Tab title="Controls">
                                    {selectedEvent.config.staticUrls.map((url) => (
                                        <Grid columns={["auto", "100px"]} align="center">
                                            <Box pad={"small"}>
                                                <Text color={staticUrl == url.url ? "brand" : "text"}>{url.name}</Text>
                                            </Box>
                                            <CustomButton icon={pointer} label="Select" onClick={() => setStaticUrl(url.url)}></CustomButton>
                                        </Grid>
                                    ))}
                                    <Box width={"100%"} gap="small">
                                        <Box direction="row" gap="small" pad="small" align="center" border justify="between">
                                            <Text>All</Text>
                                            <AgentControls onChange={(agentConfig, ledBarConfig) => handleAllAction(agentConfig, ledBarConfig)} eventConfig={selectedEvent.config}></AgentControls>
                                        </Box>
                                        {/* Droppable teams */}
                                        {teams.map((team, i) => (
                                            <Box key={i} direction="row" gap="small" fill>
                                                <Box border pad={"small"}>
                                                    <Text textAlign="center" color={"brand"}>
                                                        Team {team.name}
                                                    </Text>
                                                    <AgentControls
                                                        onChange={(agentConfig, ledBarConfig) => handleTeamAction(i, agentConfig, ledBarConfig)}
                                                        eventConfig={selectedEvent.config}
                                                    ></AgentControls>
                                                </Box>
                                                <Box gap="small">
                                                    {team.agents
                                                        .sort((a, b) => a.player - b.player)
                                                        .map((agentConfig) => (
                                                            <Box
                                                                key={agentConfig.agentID}
                                                                border
                                                                direction={size == "small" ? "column" : "row"}
                                                                gap="small"
                                                                align="center"
                                                                pad="small"
                                                                justify="between"
                                                                fill
                                                            >
                                                                {/* {JSON.stringify(agentConfig)} */}
                                                                <Text color={"brand"}>{agents.find((x) => x.id == agentConfig.agentID)?.name}</Text>
                                                                <AgentControls
                                                                    onChange={(actionConfig, ledBarConfig) => handleAction(agentConfig.agentID, actionConfig, ledBarConfig)}
                                                                    eventConfig={selectedEvent.config}
                                                                ></AgentControls>
                                                            </Box>
                                                        ))}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Tab>
                                <Tab title="Config">
                                    {selectedEvent.config.staticUrls.map((url) => (
                                        <Grid columns={["auto", "100px", "100px"]} align="center">
                                            <Box pad={"small"}>
                                                <Text color={staticUrl == url.url ? "brand" : "text"}>{url.name}</Text>
                                                <Text color={staticUrl == url.url ? "brand" : "text"}>{url.url}</Text>
                                            </Box>
                                            <CustomButton icon={pointer} label="Select" onClick={() => setStaticUrl(url.url)}></CustomButton>
                                            <CustomButton icon={minus} label="Remove" onClick={() => removeStaticUrl(url)}></CustomButton>
                                        </Grid>
                                    ))}
                                    <Grid columns={["auto", "auto", "200px"]} align="center" gap={"xsmall"}>
                                        <FormField label="Static Url Name">
                                            <TextInput value={staticUrlName} onChange={(e) => setStaticUrlName(e.target.value)} />
                                        </FormField>
                                        <FormField label="Static Url">
                                            <TextInput value={staticUrl} onChange={(e) => setStaticUrl(e.target.value)} />
                                        </FormField>
                                        <CustomButton icon={plus} label="Add Static Url" onClick={() => addStaticUrl()}></CustomButton>
                                    </Grid>
                                    {toPairs(selectedEvent.config.ledBarActions).map(([action, config]) => (
                                        <Grid columns={["auto", "100px", "100px"]} align="center" key={action}>
                                            <RenderLEDBarAction config={config} name={action}></RenderLEDBarAction>
                                            <CustomButton icon={minus} label="Remove" onClick={() => removeLEDBarAction(action)}></CustomButton>
                                        </Grid>
                                    ))}
                                    <Grid columns={["auto", "auto", "200px"]} align="center" gap={"xsmall"}>
                                        <FormField label="Action Name">
                                            <TextInput value={ledBarActionName} onChange={(e) => setLedBarActionName(e.target.value)} />
                                        </FormField>
                                        <LEDBarAction onChange={setLedBarActionConfig}></LEDBarAction>
                                        <CustomButton icon={plus} label="Add LED Bar Action" onClick={() => addLEDBarAction()}></CustomButton>
                                    </Grid>
                                    <DragDropContext onDragEnd={handleDrop}>
                                        {/* Draggable agents */}
                                        <Box gap="small">
                                            <Droppable droppableId={"x"}>
                                                {(provided) => (
                                                    <Box direction="row" gap="small" ref={provided.innerRef} {...provided.droppableProps} wrap>
                                                        {agents
                                                            .filter((a) => !selectedEvent.config.agents[a.id])
                                                            .map((agent, i) => (
                                                                <Draggable key={agent.id} draggableId={agent.id.toString()} index={i}>
                                                                    {(provided, snapshot) => (
                                                                        <Box
                                                                            ref={provided.innerRef}
                                                                            {...provided.draggableProps}
                                                                            {...provided.dragHandleProps}
                                                                            hoverIndicator={{ background: "brand" }}
                                                                            border={{
                                                                                size: "small",
                                                                                color: snapshot.isDragging ? "brand" : "transparent",
                                                                            }}
                                                                            onClick={() => toggleAgentPlacementModal(agent)}
                                                                        >
                                                                            <Box key={agent.id} direction="row" gap="small" pad={"small"} background={"background-front"}>
                                                                                <Text>{agent.name}</Text>
                                                                            </Box>
                                                                        </Box>
                                                                    )}
                                                                </Draggable>
                                                            ))}
                                                    </Box>
                                                )}
                                            </Droppable>
                                            <StyledModal isOpen={showAgentPlacementModal} setIsOpen={() => setShowAgentPlacementModal(false)}>
                                                <Box>
                                                    <FormField label="Player">
                                                        <TextInput type="number" value={placementPlayer} onChange={(e) => setPlacementPlayer(e.target.valueAsNumber)}></TextInput>
                                                    </FormField>
                                                    <FormField label="Team">
                                                        <TextInput type="number" value={placementTeam} onChange={(e) => setPlacementTeam(e.target.valueAsNumber)}></TextInput>
                                                    </FormField>
                                                    <Box direction="row" gap="small">
                                                        <CustomButton label={"Close"} onClick={() => setShowAgentPlacementModal(false)}></CustomButton>
                                                        <CustomButton primary label={"Place"} onClick={() => placeAgent()}></CustomButton>
                                                    </Box>
                                                </Box>
                                            </StyledModal>
                                            {/* Droppable teams */}
                                            {teams.map((team, i) => (
                                                <Droppable key={i} droppableId={i.toString()}>
                                                    {(provided) => (
                                                        <Box direction="row" gap="small" ref={provided.innerRef} {...provided.droppableProps}>
                                                            <Box border pad={"small"}>
                                                                <Text textAlign="center" color={"brand"}>
                                                                    Team {team.name}
                                                                </Text>
                                                            </Box>
                                                            <Box gap="small">
                                                                {team.agents
                                                                    .sort((a, b) => a.player - b.player)
                                                                    .map((agentConfig) => (
                                                                        <Box key={agentConfig.agentID} border>
                                                                            <AgentTile
                                                                                config={agentConfig}
                                                                                key={"t" + agentConfig.agentID}
                                                                                agent={agents.find((x) => x.id == agentConfig.agentID)}
                                                                                onAction={(agentConfig, ledBarConfig) => handleAction(agentConfig.agentID, agentConfig, ledBarConfig)}
                                                                            ></AgentTile>
                                                                            <Box direction="row" gap="small" pad={"small"}>
                                                                                <Box direction="row" gap="small" justify="center" align="center">
                                                                                    <Text size="small">Player: {agentConfig.player}</Text>
                                                                                    <PlusMinus value={agentConfig.player} onChange={(v) => updateAgentPlayer(agentConfig, v)}></PlusMinus>
                                                                                </Box>
                                                                                <Box direction="row" gap="small" justify="center" align="center">
                                                                                    <Text size="small">LED: {agentConfig.ledBarID}</Text>
                                                                                    <PlusMinus value={agentConfig.ledBarID || 0} onChange={(v) => updateAgentLEDID(agentConfig, v)}></PlusMinus>
                                                                                </Box>
                                                                                <CustomButton icon={minus} label="Remove" onClick={() => removeAgent(agentConfig.agentID)}></CustomButton>
                                                                            </Box>
                                                                        </Box>
                                                                    ))}
                                                            </Box>
                                                            {provided.placeholder}
                                                        </Box>
                                                    )}
                                                </Droppable>
                                            ))}
                                        </Box>
                                    </DragDropContext>
                                </Tab>
                            </Tabs>
                        </>
                    )}
                    <StyledModal isOpen={showCreateEventModal} setIsOpen={() => setShowCreateEventModal(false)}>
                        <Box width={"medium"}>
                            <FormField label="Event Name">
                                <TextInput value={newEventName} onChange={(e) => setNewEventName(e.target.value)}></TextInput>
                            </FormField>
                            <CustomButton icon={plus} primary label="Create Event" onClick={() => doCreateEvent()}></CustomButton>
                        </Box>
                    </StyledModal>
                </Box>
            )}
            {!user.hasRole("Admin") && <Text>Admin Required</Text>}
        </div>
    );
};

export default AgentsManagement;

// <Routes>
//     {/* <Route path={"create"} element={<>{hasWritePermission && <CreateAlias />}</>}></Route>
// <Route path={":id/*"} element={<AliasProfile />}></Route>
// <Route path={""} element={<AliasTable />}></Route> */}
// </Routes>
